import React from 'react';
import ContractHmrPanel from './ContractHmrPanel';
import ContractRaBillPanel from './ContractRaBillPanel';
import {
  deriveContractMoneyBreakdown,
  formatContractInr,
} from '../../utils/contractBookingDisplay';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatCurrency(value) {
  return formatContractInr(value);
}

function transportLabel(by) {
  if (by === 'client') return 'Client';
  if (by === 'owner') return 'Owner';
  if (by === 'shared') return 'Shared (record only)';
  return '—';
}

function transportAmountText(by, amount) {
  if (by === 'owner' && (amount == null || Number(amount) === 0)) {
    return 'Outside platform';
  }
  if (amount == null) return '—';
  return `₹${formatCurrency(amount)}`;
}

export default function ContractBookingStatus({
  booking,
  role = 'client',
  allowConfirm = false,
  walletUser = null,
  t = null,
}) {
  if (!booking || booking.booking_type !== 'contract') return null;

  const money = deriveContractMoneyBreakdown(booking);

  return (
    <div className="my-2 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-amber-300">
          Contract booking
        </span>
        <span className="rounded-full border border-amber-400/30 px-2 py-1 text-[10px] font-semibold text-amber-200">
          {booking.status || 'Active'}
        </span>
      </div>

      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="rounded-md border border-amber-400/20 bg-black/20 px-2.5 py-2">
          <p className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
            Total Hire Value (A)
          </p>
          <p className="m-0 text-sm font-semibold text-amber-100">
            ₹{formatCurrency(money.totalHireValueA)}
          </p>
          <p className="mb-0 mt-0.5 text-[10px] text-slate-500">
            {money.gstLabel}
            {' · '}
            taxable ₹{formatCurrency(money.hireTaxable)}
          </p>
        </div>
        <div className="rounded-md border border-amber-400/20 bg-black/20 px-2.5 py-2">
          <p className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
            Total Mobilization (B)
          </p>
          {money.mobilizationCollected ? (
            <>
              <p className="m-0 text-sm font-semibold text-amber-100">
                ₹{formatCurrency(money.totalMobilizationB)}
              </p>
              <p className="mb-0 mt-0.5 text-[10px] text-slate-500">
                {money.gstLabel}
                {' · '}
                taxable ₹{formatCurrency(money.mobTaxable)}
              </p>
            </>
          ) : (
            <p className="m-0 text-sm font-semibold text-slate-400">
              Not collected at booking
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-slate-300 md:grid-cols-4">
        <div>
          <p className="mb-1 text-[10px] uppercase text-slate-500">Contract period</p>
          <p className="m-0">{formatDate(booking.start_date)} – {formatDate(booking.end_date)}</p>
        </div>
        <div>
          <p className="mb-1 text-[10px] uppercase text-slate-500">Billing cycle</p>
          <p className="m-0 capitalize">{booking.billing_cycle || '—'}</p>
        </div>
        <div>
          <p className="mb-1 text-[10px] uppercase text-slate-500">Rate per cycle</p>
          <p className="m-0">₹{formatCurrency(booking.fixed_rate_per_cycle)}</p>
        </div>
        <div>
          <p className="mb-1 text-[10px] uppercase text-slate-500">Guaranteed hours / cycle</p>
          <p className="m-0">
            {booking.guaranteed_hours_per_cycle != null
              ? `${formatCurrency(booking.guaranteed_hours_per_cycle)} h`
              : '—'}
          </p>
        </div>
        <div>
          <p className="mb-1 text-[10px] uppercase text-slate-500">Mobilization transport</p>
          <p className="m-0">
            {transportLabel(booking.mobilization_transport_by)}
            {' · '}
            {transportAmountText(
              booking.mobilization_transport_by,
              booking.mobilization_transport_amount,
            )}
          </p>
        </div>
        <div>
          <p className="mb-1 text-[10px] uppercase text-slate-500">Demobilization transport</p>
          <p className="m-0">
            {transportLabel(booking.demobilization_transport_by)}
            {' · '}
            {transportAmountText(
              booking.demobilization_transport_by,
              booking.demobilization_transport_amount,
            )}
          </p>
        </div>
        <div>
          <p className="mb-1 text-[10px] uppercase text-slate-500">Contract total</p>
          <p className="m-0">₹{formatCurrency(booking.total_amount)}</p>
        </div>
        <div>
          <p className="mb-1 text-[10px] uppercase text-slate-500">Advance collected</p>
          <p className="m-0">₹{formatCurrency(booking.advance_paid)}</p>
        </div>
      </div>
      <p className="mb-0 mt-3 text-[11px] font-medium text-amber-200">
        Cycle RA-bills use a fresh wallet payment after acceptance. Transport is one-time (not per cycle).
      </p>
      <ContractHmrPanel
        booking={booking}
        role={role}
        allowConfirm={allowConfirm || role === 'client'}
      />
      <ContractRaBillPanel
        booking={booking}
        role={role}
        walletUser={walletUser}
        t={t}
      />
    </div>
  );
}
